import React, { useState } from "react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  CheckCircle2,
  Home,
  Tractor,
  ExternalLink,
  Building,
  FileText,
} from "lucide-react";
import { useTranslation } from "@/context/LanguageContext";

const AGRI_IMG =
  "https://images.unsplash.com/photo-1623211265647-acf8ad307e0b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzh8MHwxfHNlYXJjaHwyfHxpbmRpYW4lMjBmYXJtZXIlMjBhZ3JpY3VsdHVyZSUyMGZpZWxkfGVufDB8fHx8MTc3NzcwMzY4OXww&ixlib=rb-4.1.0&q=85";

const SLAB_AMTS = ["₹30,000", "₹60,000", "₹78,000"];

export default function SubsidySection() {
  const { t } = useTranslation();
  const slabLabels = t("subsidy.slab_labels");
  const slabNotes = t("subsidy.slab_notes");
  const resBullets = t("subsidy.res_bullets");

  const indExtras = t("subsidy.ind_extras");
  const indRows = t("subsidy.ind_example_rows");

  const ghsTitle = t("subsidy.ghs_title");
  const ghsIntro = t("subsidy.ghs_intro");
  const ghsStructure = t("subsidy.ghs_structure");
  const ghsStructureLabel = t("subsidy.ghs_structure_label");
  const ghsEligibility = t("subsidy.ghs_eligibility");
  const ghsEligibilityLabel = t("subsidy.ghs_eligibility_label");
  const ghsRows = t("subsidy.ghs_example_rows");
  const ghsDocs = t("subsidy.ghs_documents");

  const agriComps = t("subsidy.agri_components");
  const agriBullets = t("subsidy.agri_bullets");

  const [resSubTab, setResSubTab] = useState("individual");

  return (
    <section
      id="subsidy"
      data-testid="subsidy-section"
      className="py-20 md:py-28 warm-bg border-y border-green-100"
    >
      <div className="max-w-7xl mx-auto px-5 md:px-10">
        <div className="max-w-2xl">
          <span className="text-green-700 font-semibold tracking-widest text-xs uppercase">
            {t("subsidy.eyebrow")}
          </span>
          <h2 className="mt-3 font-display font-extrabold text-4xl lg:text-5xl text-stone-900">
            {t("subsidy.title_pre")}{" "}
            <span className="text-green-700">{t("subsidy.title_hl")}</span>{" "}
            {t("subsidy.title_post")}
          </h2>
          <p className="mt-4 text-stone-600 leading-relaxed">
            {t("subsidy.subtitle")}
          </p>
        </div>

        <Tabs defaultValue="residential" className="mt-10" data-testid="subsidy-tabs">
          <TabsList className="bg-white border border-stone-200 rounded-full p-1 h-auto premium-shadow">
            <TabsTrigger
              value="residential"
              data-testid="tab-residential"
              className="rounded-full px-5 py-2.5 data-[state=active]:bg-green-600 data-[state=active]:text-white font-semibold text-sm flex items-center gap-2"
            >
              <Home className="w-4 h-4" /> {t("subsidy.tab_res")}
            </TabsTrigger>
            <TabsTrigger
              value="agriculture"
              data-testid="tab-agriculture"
              className="rounded-full px-5 py-2.5 data-[state=active]:bg-green-600 data-[state=active]:text-white font-semibold text-sm flex items-center gap-2"
            >
              <Tractor className="w-4 h-4" /> {t("subsidy.tab_agri")}
            </TabsTrigger>
          </TabsList>

          {/* ============ RESIDENTIAL (PMSGY) ============ */}
          <TabsContent value="residential" className="mt-8">
            {/* Sub-tabs: Individual vs Society */}
            <div
              className="inline-flex p-1 rounded-full bg-white border border-stone-200 premium-shadow mb-7"
              data-testid="subsidy-subtabs"
            >
              <button
                onClick={() => setResSubTab("individual")}
                data-testid="subtab-individual"
                className={`rounded-full px-4 md:px-5 py-2 text-sm font-semibold flex items-center gap-2 transition ${
                  resSubTab === "individual"
                    ? "bg-green-600 text-white shadow"
                    : "text-stone-700 hover:text-green-700"
                }`}
              >
                <Home className="w-4 h-4" /> {t("subsidy.sub_individual")}
              </button>
              <button
                onClick={() => setResSubTab("society")}
                data-testid="subtab-society"
                className={`rounded-full px-4 md:px-5 py-2 text-sm font-semibold flex items-center gap-2 transition ${
                  resSubTab === "society"
                    ? "bg-green-600 text-white shadow"
                    : "text-stone-700 hover:text-green-700"
                }`}
              >
                <Building className="w-4 h-4" /> {t("subsidy.sub_society")}
              </button>
            </div>

            <div className="grid lg:grid-cols-5 gap-8">
              {/* LEFT: explanatory card */}
              {resSubTab === "individual" ? (
                <div
                  className="lg:col-span-3 bg-white rounded-3xl border border-stone-200 p-7 premium-shadow"
                  data-testid="subsidy-individual-card"
                >
                  <h3 className="font-display font-bold text-2xl text-stone-900">
                    {t("subsidy.res_title")}
                  </h3>
                  <p className="mt-2 text-stone-600 text-[15px]">
                    {t("subsidy.res_desc")}
                  </p>
                  <div className="mt-6 grid sm:grid-cols-3 gap-3">
                    {slabLabels.map((lbl, i) => (
                      <div
                        key={i}
                        className="rounded-2xl border border-green-200 bg-green-50/60 p-4"
                      >
                        <div className="text-xs text-stone-500 uppercase tracking-wider">
                          {lbl}
                        </div>
                        <div className="mt-1 font-display font-extrabold text-2xl text-green-800 digit-stat">
                          {SLAB_AMTS[i]}
                        </div>
                        <div className="text-xs text-stone-500 mt-1">
                          {slabNotes[i]}
                        </div>
                      </div>
                    ))}
                  </div>
                  <ul className="mt-6 space-y-2 text-sm text-stone-700">
                    {resBullets.map((txt, i) => (
                      <li key={i} className="flex gap-2">
                        <CheckCircle2 className="w-4 h-4 text-green-600 mt-0.5 shrink-0" />
                        <span>{txt}</span>
                      </li>
                    ))}
                  </ul>
                  <a
                    href="https://pmsuryaghar.gov.in/"
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1 mt-6 text-green-700 font-semibold text-sm hover:underline"
                    data-testid="subsidy-gov-link"
                  >
                    {t("subsidy.read_official")}{" "}
                    <ExternalLink className="w-3.5 h-3.5" />
                  </a>
                </div>
              ) : (
                <div
                  className="lg:col-span-3 bg-white rounded-3xl border border-stone-200 p-7 premium-shadow"
                  data-testid="subsidy-society-card"
                >
                  <div className="flex items-start gap-3">
                    <div className="w-12 h-12 rounded-2xl bg-green-100 grid place-items-center flex-shrink-0">
                      <Building className="w-6 h-6 text-green-700" strokeWidth={2.2} />
                    </div>
                    <div>
                      <h3 className="font-display font-bold text-2xl text-stone-900">
                        {ghsTitle}
                      </h3>
                      <p className="mt-1 text-stone-600 text-[15px] leading-relaxed">
                        {ghsIntro}
                      </p>
                    </div>
                  </div>
                  <div className="my-6 premium-divider" />
                  <div className="grid sm:grid-cols-2 gap-6">
                    <div>
                      <div className="text-[11px] uppercase tracking-wider text-green-700 font-semibold">
                        {ghsStructureLabel}
                      </div>
                      <ul className="mt-3 space-y-2 text-sm text-stone-700">
                        {ghsStructure.map((s, i) => (
                          <li key={i} className="flex gap-2">
                            <CheckCircle2 className="w-4 h-4 text-green-600 mt-0.5 shrink-0" />
                            <span>{s}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div>
                      <div className="text-[11px] uppercase tracking-wider text-green-700 font-semibold">
                        {ghsEligibilityLabel}
                      </div>
                      <ul className="mt-3 space-y-2 text-sm text-stone-700">
                        {ghsEligibility.map((s, i) => (
                          <li key={i} className="flex gap-2">
                            <CheckCircle2 className="w-4 h-4 text-green-600 mt-0.5 shrink-0" />
                            <span>{s}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>

                  <div className="my-6 premium-divider" />
                  <div>
                    <div className="text-[11px] uppercase tracking-wider text-green-700 font-semibold flex items-center gap-2">
                      <FileText className="w-3.5 h-3.5" />{" "}
                      {t("subsidy.ghs_documents_label")}
                    </div>
                    <ul className="mt-3 space-y-2 text-sm text-stone-700">
                      {ghsDocs.map((d, i) => (
                        <li key={i} className="flex gap-2">
                          <CheckCircle2 className="w-4 h-4 text-green-600 mt-0.5 shrink-0" />
                          <span>{d}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <a
                    href="https://pmsuryaghar.gov.in/"
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1 mt-6 text-green-700 font-semibold text-sm hover:underline"
                  >
                    {t("subsidy.read_official")}{" "}
                    <ExternalLink className="w-3.5 h-3.5" />
                  </a>
                </div>
              )}

              {/* RIGHT: green savings box (variants) */}
              {resSubTab === "individual" ? (
                <div
                  className="lg:col-span-2 bg-gradient-to-br from-green-700 to-green-900 text-white rounded-3xl p-7 relative overflow-hidden premium-shadow"
                  data-testid="subsidy-individual-box"
                >
                  <div className="absolute -top-12 -right-12 w-56 h-56 rounded-full bg-yellow-400/25 blur-2xl pointer-events-none" />
                  <div className="absolute -bottom-16 -left-12 w-56 h-56 rounded-full bg-orange-400/15 blur-2xl pointer-events-none" />
                  <div className="relative">
                    <div className="text-xs uppercase tracking-widest opacity-80">
                      {t("subsidy.ind_box_label")}
                    </div>
                    <div className="mt-2 font-display font-black text-[44px] leading-none digit-stat">
                      {t("subsidy.ind_box_amount")}
                    </div>
                    <p className="mt-3 text-sm opacity-90 max-w-[280px] leading-relaxed">
                      {t("subsidy.ind_box_caption")}
                    </p>
                    <div className="mt-6 bg-white/10 rounded-2xl p-4 backdrop-blur border border-white/10">
                      <div className="text-[11px] uppercase tracking-wider opacity-75">
                        {t("subsidy.ind_example_title")}
                      </div>
                      <div className="mt-3 grid grid-cols-2 gap-3 text-sm">
                        {indRows.map((r, i) => (
                          <div key={i}>
                            <div className="opacity-75 text-xs">{r.label}</div>
                            <div className="font-bold digit-stat">{r.value}</div>
                          </div>
                        ))}
                        <div className="col-span-2 pt-3 border-t border-white/20">
                          <div className="opacity-75 text-xs">
                            {t("subsidy.ind_example_net_label")}
                          </div>
                          <div className="font-display font-extrabold text-2xl digit-stat">
                            {t("subsidy.ind_example_net_value")}
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="mt-5">
                      <div className="text-[11px] uppercase tracking-wider opacity-75 mb-2">
                        {t("subsidy.ind_extras_label")}
                      </div>
                      <ul className="space-y-1.5 text-sm">
                        {indExtras.map((x, i) => (
                          <li key={i} className="flex gap-2">
                            <CheckCircle2 className="w-4 h-4 text-yellow-300 mt-0.5 shrink-0" />
                            <span className="opacity-95">{x}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              ) : (
                <div
                  className="lg:col-span-2 bg-gradient-to-br from-green-700 to-green-900 text-white rounded-3xl p-7 relative overflow-hidden premium-shadow"
                  data-testid="subsidy-society-box"
                >
                  <div className="absolute -top-12 -right-12 w-56 h-56 rounded-full bg-yellow-400/25 blur-2xl pointer-events-none" />
                  <div className="absolute -bottom-16 -left-12 w-56 h-56 rounded-full bg-orange-400/15 blur-2xl pointer-events-none" />
                  <div className="relative">
                    <div className="text-xs uppercase tracking-widest opacity-80">
                      {t("subsidy.ghs_box_label")}
                    </div>
                    <div className="mt-2 font-display font-black text-[40px] leading-none digit-stat">
                      {t("subsidy.ghs_box_amount")}
                    </div>
                    <p className="mt-3 text-sm opacity-90 max-w-[300px] leading-relaxed">
                      {t("subsidy.ghs_box_caption")}
                    </p>
                    <div className="mt-6 bg-white/10 rounded-2xl p-4 backdrop-blur border border-white/10">
                      <div className="text-[11px] uppercase tracking-wider opacity-75">
                        {t("subsidy.ghs_example_title")}
                      </div>
                      <div className="mt-3 space-y-2.5 text-sm">
                        {ghsRows.map((r, i) => (
                          <div key={i} className="flex items-baseline justify-between gap-3">
                            <span className="opacity-85 text-xs">{r.label}</span>
                            <span className="font-bold digit-stat whitespace-nowrap">{r.value}</span>
                          </div>
                        ))}
                        <div className="pt-3 border-t border-white/20 flex items-baseline justify-between gap-3">
                          <span className="opacity-85 text-xs">
                            {t("subsidy.ghs_example_net_label")}
                          </span>
                          <span className="font-display font-extrabold text-xl digit-stat whitespace-nowrap">
                            {t("subsidy.ghs_example_net_value")}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="mt-4 inline-flex items-center gap-2 bg-orange-500/90 text-white rounded-full px-3 py-1.5 text-[11px] font-bold tracking-wider uppercase">
                      {t("subsidy.ghs_example_payback")}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </TabsContent>

          {/* ============ AGRICULTURE ============ */}
          <TabsContent value="agriculture" className="mt-8">
            <div className="grid lg:grid-cols-5 gap-8">
              <div className="lg:col-span-3 bg-white rounded-3xl border border-stone-200 p-7 premium-shadow">
                <h3 className="font-display font-bold text-2xl text-stone-900">
                  {t("subsidy.agri_title")}
                </h3>
                <p className="mt-2 text-stone-600 text-[15px]">
                  {t("subsidy.agri_desc")}
                </p>
                <div className="mt-6 grid sm:grid-cols-3 gap-3">
                  {agriComps.map((x) => (
                    <div
                      key={x.k}
                      className="rounded-2xl border border-green-200 bg-green-50/60 p-4"
                    >
                      <div className="text-xs text-stone-500 uppercase tracking-wider">
                        {x.k}
                      </div>
                      <div className="mt-1 font-display font-bold text-sm text-stone-800 leading-snug">
                        {x.v}
                      </div>
                    </div>
                  ))}
                </div>
                <ul className="mt-6 space-y-2 text-sm text-stone-700">
                  {agriBullets.map((txt, i) => (
                    <li key={i} className="flex gap-2">
                      <CheckCircle2 className="w-4 h-4 text-green-600 mt-0.5 shrink-0" />
                      <span>{txt}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="lg:col-span-2 rounded-3xl overflow-hidden relative min-h-[360px] border border-stone-200 premium-shadow">
                <img
                  src={AGRI_IMG}
                  alt="Indian farmer field"
                  className="absolute inset-0 w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-green-900/85 via-green-900/40 to-transparent" />
                <div className="absolute bottom-0 inset-x-0 p-6 text-white">
                  <div className="text-xs uppercase tracking-widest opacity-80">
                    {t("subsidy.farmer_benefit")}
                  </div>
                  <div className="mt-1 font-display font-black text-3xl">
                    {t("subsidy.farmer_amount")}
                  </div>
                  <p className="text-sm opacity-90 mt-2">
                    {t("subsidy.farmer_note")}
                  </p>
                </div>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </section>
  );
}
